import { KNOWN_PAGE_KEYS } from '../data/analytics.config';
import { LEGAL_DOC_KINDS } from '../data/legal.config';
import { PRODUCT_CONFIG } from '../data/products.config';
import type { ContentGroup, ResolvedPath } from '../models/analytics.model';
import type { ProductSlug } from '../models/product.model';
import { canonicalPath } from './locale-path';

const GUIDE_PAGE = /^\/catalog\/guide\/([^/]+)$/;
const LEGAL_PAGE = /^\/(catalog|core)\/legal\/([^/]+)$/;

/** The path alone: everything from the first `;` (matrix params), `?` or `#` is dropped. */
export function pathnameOf(url: string): string {
  const at = url.search(/[;?#]/);
  return at === -1 ? url : url.slice(0, at);
}

/** The query string of `url`, without its fragment. */
export function queryOf(url: string): URLSearchParams {
  const path = url.split('#')[0];
  const at = path.indexOf('?');
  return new URLSearchParams(at === -1 ? '' : path.slice(at + 1));
}

/**
 * What a visited path is, as the tracking plan names it. Visitor-typed slugs never become
 * a page key: an unlisted guide slug or legal slug resolves to nothing or to not-found.
 */
export function resolvePath(url: string): ResolvedPath {
  const path = canonicalPath(pathnameOf(url));
  if (KNOWN_PAGE_KEYS.has(path)) return { kind: 'page', pageKey: path };

  const guide = GUIDE_PAGE.exec(path);
  if (guide) {
    const listed = PRODUCT_CONFIG.find((p) => p.slug === 'catalog')?.guidePages.some(
      (page) => page.slug === guide[1],
    );
    return listed
      ? { kind: 'page', pageKey: '/catalog/guide/:page', guideSlug: guide[1] }
      : { kind: 'unlisted-guide' };
  }

  const legal = LEGAL_PAGE.exec(path);
  if (legal && Object.hasOwn(LEGAL_DOC_KINDS, legal[2])) {
    const product = legal[1] as ProductSlug;
    return { kind: 'legal', pageKey: `/${product}/legal/:doc`, product, doc: legal[2] };
  }

  return { kind: 'not-found' };
}

/** The page key a link leads to, for `destination`; an address no page answers is `/404`. */
export function destinationKeyOf(href: string): string {
  const resolved = resolvePath(href);
  if (resolved.kind === 'page' || resolved.kind === 'legal') return resolved.pageKey;
  return '/404';
}

export function contentGroupOf(pageKey: string): ContentGroup {
  if (pageKey === '/core/agent') return 'agent';
  if (pageKey === '/catalog' || pageKey.startsWith('/catalog/')) return 'catalog';
  if (pageKey === '/core' || pageKey.startsWith('/core/')) return 'core';
  return 'site';
}
